import { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";
import { ensureTheraLocationPermission } from "../../../../lib/location-recorder";
import type { TrackPoint } from "../../../../lib/offline-draft";
import { startGeoArStandalonePosition, type StopGeoArStandalonePosition } from "./geoArStandalonePosition";

type DeviceOrientationWithCompass = DeviceOrientationEvent & {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
};

type OrientationCtorWithPermission = typeof DeviceOrientationEvent & {
  requestPermission?: () => Promise<"granted" | "denied">;
};

type NativeCameraPose = {
  cameraAzimuth: number;
  magneticAzimuth: number;
  cameraElevation: number;
  roll: number;
  headingAccuracy: number;
  screenRotation: 0 | 90 | 180 | 270;
  horizontalStrength: number;
  stableForPointIdentify: boolean;
  declination: number;
  trueNorthCorrected: boolean;
  sensorAccuracy: number;
  source: "ANDROID_ROTATION_VECTOR_CAMERA_FORWARD";
  timestamp: number;
};

type TheraCameraPosePlugin = {
  start(): Promise<{ sensor: string; active: boolean }>;
  stop(): Promise<void>;
  updateLocation(options: { latitude: number; longitude: number; altitude?: number }): Promise<{ declination: number; reference: string }>;
  getCurrentPose(): Promise<({ available: false } | ({ available: true } & NativeCameraPose))>;
  addListener(eventName: "pose", listener: (pose: NativeCameraPose) => void): Promise<PluginListenerHandle>;
};

const TheraCameraPose = registerPlugin<TheraCameraPosePlugin>("TheraCameraPose");

export type GeoArFieldOrientation = {
  /** True-north camera-forward azimuth when the Android native adapter is available. */
  heading: number | null;
  headingAccuracy: number | null;
  /** Camera optical-axis elevation: 0° horizon, + up, - down. */
  pitch: number | null;
  roll: number | null;
  rawAlpha: number | null;
  rawBeta: number | null;
  rawGamma: number | null;
  screenRotation: 0 | 90 | 180 | 270;
  absolute: boolean;
  capturedAtMs: number;
  source: "ANDROID_ROTATION_VECTOR_CAMERA_FORWARD" | "WEB_DEVICE_ORIENTATION";
  horizontalStrength: number | null;
  stableForPointIdentify: boolean;
  magneticHeading: number | null;
  magneticDeclination: number | null;
  trueNorthCorrected: boolean;
};

const normalizeHeading = (value: number) => ((value % 360) + 360) % 360;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

function getScreenRotation(): 0 | 90 | 180 | 270 {
  const raw = Number(globalThis.screen?.orientation?.angle ?? 0);
  const normalized = ((raw % 360) + 360) % 360;
  if (normalized === 90 || normalized === 180 || normalized === 270) return normalized;
  return 0;
}

export async function requestGeoArOrientationPermission(): Promise<boolean> {
  const ctor = globalThis.DeviceOrientationEvent as OrientationCtorWithPermission | undefined;
  if (!ctor) return false;
  if (typeof ctor.requestPermission !== "function") return true;
  try {
    return (await ctor.requestPermission()) === "granted";
  } catch {
    return false;
  }
}

/**
 * Camera-forward orientation adapter.
 *
 * Android uses the native Rotation Vector sensor and transforms the rear-camera
 * optical axis (-Z in device coordinates) into East/North/Up world coordinates.
 * This avoids the old flat-phone assumption where the top edge of the handset
 * was treated as the camera direction. Web/PWA keeps the existing
 * DeviceOrientation path as a compatibility fallback.
 */
export function useGeoArFieldOrientation(
  active: boolean,
  headingOffset: number,
  pitchOffset: number,
  positionHint: TrackPoint | null = null,
) {
  const [reading, setReading] = useState<GeoArFieldOrientation | null>(null);
  const nativeLastAtRef = useRef(0);

  useEffect(() => {
    if (!active || Capacitor.getPlatform() !== "android" || !positionHint) return;
    void TheraCameraPose.updateLocation({
      latitude: positionHint.latitude,
      longitude: positionHint.longitude,
      altitude: positionHint.altitude ?? 0,
    }).catch(() => undefined);
  }, [active, positionHint?.latitude, positionHint?.longitude, positionHint?.altitude]);

  useEffect(() => {
    if (!active || Capacitor.getPlatform() !== "android") return;
    let cancelled = false;
    let listener: PluginListenerHandle | null = null;

    const applyNativePose = (pose: NativeCameraPose) => {
      if (cancelled) return;
      nativeLastAtRef.current = Date.now();
      setReading({
        heading: normalizeHeading(pose.cameraAzimuth + headingOffset),
        headingAccuracy: Number.isFinite(pose.headingAccuracy) ? Math.abs(pose.headingAccuracy) : null,
        pitch: clamp(pose.cameraElevation + pitchOffset, -90, 90),
        roll: clamp(pose.roll, -180, 180),
        rawAlpha: null,
        rawBeta: null,
        rawGamma: null,
        screenRotation: pose.screenRotation,
        absolute: true,
        capturedAtMs: pose.timestamp || Date.now(),
        source: "ANDROID_ROTATION_VECTOR_CAMERA_FORWARD",
        horizontalStrength: Number.isFinite(pose.horizontalStrength) ? pose.horizontalStrength : null,
        stableForPointIdentify: Boolean(pose.stableForPointIdentify),
        magneticHeading: Number.isFinite(pose.magneticAzimuth) ? pose.magneticAzimuth : null,
        magneticDeclination: Number.isFinite(pose.declination) ? pose.declination : null,
        trueNorthCorrected: Boolean(pose.trueNorthCorrected),
      });
    };

    const startNative = async () => {
      try {
        const nextListener = await TheraCameraPose.addListener("pose", applyNativePose);
        if (cancelled) {
          await nextListener.remove();
          return;
        }
        listener = nextListener;
        await TheraCameraPose.start();
        if (cancelled) {
          await listener.remove();
          listener = null;
          await TheraCameraPose.stop().catch(() => undefined);
          return;
        }
        const current = await TheraCameraPose.getCurrentPose().catch(() => ({ available: false } as const));
        if (current.available) applyNativePose(current);
      } catch {
        // The Web DeviceOrientation listener below remains active as fallback.
      }
    };
    // React StrictMode probes mount/unmount once in development. Delaying one
    // tick prevents that probe from acquiring the native sensor unnecessarily.
    const timer = window.setTimeout(() => void startNative(), 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      nativeLastAtRef.current = 0;
      void listener?.remove();
      void TheraCameraPose.stop().catch(() => undefined);
    };
  }, [active, headingOffset, pitchOffset]);

  useEffect(() => {
    if (!active || typeof window === "undefined" || !("DeviceOrientationEvent" in window)) {
      setReading(null);
      return;
    }
    let lastAbsoluteAt = 0;
    const handle = (event: Event) => {
      // On Android, prefer the native camera-forward vector whenever it has
      // delivered a recent sample. This listener is only a fallback.
      if (Capacitor.getPlatform() === "android" && Date.now() - nativeLastAtRef.current < 1500) return;

      const value = event as DeviceOrientationWithCompass;
      const now = Date.now();
      const isAbsolute = value.absolute === true || event.type === "deviceorientationabsolute";
      if (!isAbsolute && now - lastAbsoluteAt < 1000) return;
      if (isAbsolute) lastAbsoluteAt = now;
      const rotation = getScreenRotation();
      const webkitHeading = value.webkitCompassHeading;
      const rawAlpha = value.alpha;
      let heading: number | null = null;
      if (typeof webkitHeading === "number" && Number.isFinite(webkitHeading)) {
        heading = normalizeHeading(webkitHeading + headingOffset);
      } else if (typeof rawAlpha === "number" && Number.isFinite(rawAlpha)) {
        heading = normalizeHeading(360 - rawAlpha + rotation + headingOffset);
      }
      const beta = typeof value.beta === "number" && Number.isFinite(value.beta) ? value.beta : null;
      const gamma = typeof value.gamma === "number" && Number.isFinite(value.gamma) ? value.gamma : null;
      const naturalPitch = beta === null ? null : beta - 90;
      let pitch = naturalPitch;
      let roll = gamma;
      if (naturalPitch !== null && gamma !== null) {
        if (rotation === 90) { pitch = -gamma; roll = naturalPitch; }
        if (rotation === 180) { pitch = -naturalPitch; roll = -gamma; }
        if (rotation === 270) { pitch = gamma; roll = -naturalPitch; }
      }
      const correctedPitch = pitch === null ? null : clamp(pitch + pitchOffset, -90, 90);
      setReading({
        heading,
        headingAccuracy: typeof value.webkitCompassAccuracy === "number" && Number.isFinite(value.webkitCompassAccuracy)
          ? Math.abs(value.webkitCompassAccuracy)
          : null,
        pitch: correctedPitch,
        roll: roll === null ? null : clamp(roll, -180, 180),
        rawAlpha: rawAlpha ?? null,
        rawBeta: beta,
        rawGamma: gamma,
        screenRotation: rotation,
        absolute: isAbsolute,
        capturedAtMs: now,
        source: "WEB_DEVICE_ORIENTATION",
        horizontalStrength: null,
        stableForPointIdentify: correctedPitch === null ? false : Math.abs(correctedPitch) <= 77,
        magneticHeading: heading,
        magneticDeclination: null,
        trueNorthCorrected: false,
      });
    };
    window.addEventListener("deviceorientationabsolute", handle, true);
    window.addEventListener("deviceorientation", handle, true);
    return () => {
      window.removeEventListener("deviceorientationabsolute", handle, true);
      window.removeEventListener("deviceorientation", handle, true);
    };
  }, [active, headingOffset, pitchOffset]);

  return reading;
}

export function useGeoArFieldPosition(active: boolean) {
  const [position, setPosition] = useState<TrackPoint | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    if (!active) {
      setPosition(null);
      setError(null);
      return;
    }
    let cancelled = false;
    let stop: StopGeoArStandalonePosition | null = null;
    const start = async () => {
      try {
        await ensureTheraLocationPermission();
        if (cancelled) return;
        const nextStop = await startGeoArStandalonePosition({
          onPosition(next) {
            if (cancelled) return;
            setPosition(next);
            setError(null);
          },
          onError(message) {
            if (!cancelled) setError(message);
          },
        });
        if (cancelled) {
          await nextStop();
          return;
        }
        stop = nextStop;
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : "GPS_READ_FAILED");
      }
    };
    // Avoid acquiring the native listener during React StrictMode's probe mount.
    const timer = window.setTimeout(() => void start(), 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      void stop?.();
    };
  }, [active]);

  const sourceDevice = Capacitor.getPlatform() === "android" ? "THERA_ANDROID_GPS" : "THERA_WEB_GPS";
  return useMemo(() => ({ position, error, online, sourceDevice }), [position, error, online, sourceDevice]);
}
